import React, { useState, useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import { Modal, Button } from 'react-bootstrap';
import moment from 'moment';
import { cmp, getShift } from '../helpers/misc';
import { v4 as uuid } from 'uuid';


const mapStateToProps = state => ({
  colorScheme: state.main.colorScheme,
  machineData: state.app.machineData,
  locatorFilter: state.app.locatorFilter,
  locationData: state.app.locationData,
});

const JobChangeModal = props => {
  const {
    colorScheme,
    machineData,
    locatorFilter,
    locationData,
    show,
    onHide,
  } = props;
  const [shiftChanges, setShiftChanges] = useState([]);
  
  useEffect(() => {
    if(!show) return;
    const locationObj = locationData ? locationData.get(locatorFilter) : null;
    const filteredMachineData = machineData ?
      machineData.filter(machineObj => locatorFilter === 'ALL' || machineObj.machineLocator === locatorFilter) :
      null;
    const changes = filteredMachineData.map(machineObj => machineObj.schedule.filter(o => !o.isStoppage)
      .map((scheduleObj, index) => {
        let currentJob;
        if(index === 0) {
          const curJobs = machineObj.jobs.find(jobArr => jobArr.some(job => job.actualEndDate === 0));
          const curJobId = curJobs ? curJobs.map(j => j.jobNumber).join('|') : null;
          if(curJobId) {
            if(curJobId !== scheduleObj.schedulingId) currentJob = {
              scheduleObj: {
                dynamicStartTime: scheduleObj.startTime,
                endTime: scheduleObj.startTime,
                jobNumbers: curJobs.map(j => j.jobNumber),
                machineCommonName: scheduleObj.machineCommonName,
                machineLocator: scheduleObj.machineLocator,
                machineSerialNumber: scheduleObj.machineSerialNumber,
                schedulingId: curJobId,
                startTime: scheduleObj.startTime,
              },
              jobArr: curJobs,
            };
          }
          else currentJob = {
            scheduleObj: {
              dynamicStartTime: scheduleObj.startTime,
              endTime: scheduleObj.startTime,
              jobNumbers: ['NONE'],
              machineCommonName: scheduleObj.machineCommonName,
              machineLocator: scheduleObj.machineLocator,
              machineSerialNumber: scheduleObj.machineSerialNumber,
              schedulingId: uuid(),
              startTime: scheduleObj.startTime,
            },
            jobArr: 'NONE',
          };
        }
        const jobArr = machineObj.jobs.find(arr => arr.some(obj => scheduleObj.jobNumbers.includes(obj.jobNumber)));
        if(!jobArr) return [currentJob, null];
        return [
          currentJob,
          {
            scheduleObj,
            jobArr,
          },
        ];
      })
      .flat()
      .filter(o => o != null)
      .map(({ scheduleObj, jobArr }, index, arr) => {
        if(index === arr.length - 1) return null;
        const nextJobRecords = arr[index + 1]?.jobArr;
        const nextTooNumber = nextJobRecords && nextJobRecords.length ? nextJobRecords[0].toolNumber : null;
        const nextJobNumber = nextJobRecords && nextJobRecords.length ? nextJobRecords.map(job => job.jobNumber).join(' & ') : null;
        const nextPartNumber = nextJobRecords && nextJobRecords.length ? nextJobRecords.map(job => job.partNumber).join(' & ') : null;
        const nextMaterialNumber = nextJobRecords && nextJobRecords.length && nextJobRecords[0].partMaterials ? nextJobRecords[0].partMaterials.map(o => o.materialNumber).sort().join(' & ') : null;
        const shiftData = getShift(arr[index + 1].scheduleObj.startTime, machineObj.standards.timeZone, machineObj.standards.shiftTimes);
        
        let previousToolNumber, previousJobNumber, previousPartNumber, previousMaterialNumber;
        if(jobArr === 'NONE') {
          previousToolNumber = 'NONE';
          previousJobNumber = 'NONE';
          previousPartNumber = 'NONE';
          previousMaterialNumber = 'NONE';
        }
        else {
          previousToolNumber = jobArr && jobArr.length ? jobArr[0].toolNumber : null;
          previousJobNumber = jobArr && jobArr.length ? jobArr.map(job => job.jobNumber).join(' & ') : null;
          previousPartNumber = jobArr && jobArr.length ? jobArr.map(job => job.partNumber).join(' & ') : null;
          previousMaterialNumber = jobArr && jobArr.length && jobArr[0].partMaterials ? jobArr[0].partMaterials.map(o => o.materialNumber).sort().join(' & ') : null;
        }
        
        if(!previousToolNumber || !nextTooNumber || !previousJobNumber || !nextJobNumber ||
          !previousPartNumber || !nextPartNumber || !previousMaterialNumber || !nextMaterialNumber) return null;
        return {
          changeTime: arr[index + 1].scheduleObj.startTime,
          machine: machineObj.serialNumber,
          machineCommonName: machineObj.commonName,
          changeType: previousToolNumber !== nextTooNumber ?
            'MOLD' :
            previousMaterialNumber !== nextMaterialNumber ?
              'COLOR' :
              'JOB',
          previousToolNumber,
          nextTooNumber,
          previousJobNumber,
          nextJobNumber,
          previousPartNumber,
          nextPartNumber,
          previousMaterialNumber,
          nextMaterialNumber,
          ...shiftData,
        };
      })).flat().filter(obj => obj != null).sort((a, b) => cmp(a.changeTime, b.changeTime));
    const now = Date.now();
    if(locationObj) {
      const shiftsForward = 12;
      const shiftJobChanges = [];
      for(let i = 0; i < shiftsForward; i++) {
        const shiftData = getShift(now, locationObj.timeZone, locationObj.shiftTimes, i);
        const sChanges = changes.filter(obj => (i === 0 && obj.changeTime <= now) ||
          (obj.shiftName === shiftData.shiftName && obj.shiftDate === shiftData.shiftDate));
        shiftJobChanges.push({
          shiftTitle: `${shiftData.shiftDate} | SHIFT ${shiftData.shiftName}`,
          shiftChanges: sChanges,
        });
      }
      setShiftChanges(shiftJobChanges);
    }
    else setShiftChanges([]);
  }, [locationData, machineData, locatorFilter, show]);
  
  return (
    <Modal
      size="lg"
      centered
      show={show}
      onHide={() => onHide()}
      className={`modal-content-${colorScheme.toUpperCase()}`}
    >
      <Modal.Header>
        <Modal.Title>
          JOB CHANGES
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={styles.modalBody}>
        {shiftChanges?.map(change => (
          <Fragment key={change.shiftTitle}>
            <h5 style={styles.title}>{`${change.shiftTitle} -> ${change.shiftChanges.length}`}</h5>
            {change.shiftChanges.map((obj, index) => (
              <p key={index} style={styles.text}>
                {`${moment(obj.changeTime).format('MM/DD h:mmA')} -> ${obj.machineCommonName} | ${obj.changeType} -> ${obj.previousJobNumber} (${obj.previousMaterialNumber}) -> ${obj.nextJobNumber} (${obj.nextMaterialNumber})`}
              </p>
            ))}
          </Fragment>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() => onHide()}
        >
          OK
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const styles = {
  title: {
    marginTop: 20,
    marginBottom: 5,
  },
  text: {
    margin: 0,
  },
  modalBody: {
    paddingTop: 0,
  },
};

export default connect(mapStateToProps)(JobChangeModal);